import api from '@/utils/axios';
import { unwrapItem, unwrapList } from '@/utils/apiHelpers';
import type { ApiDeliveryType, ApiOrderRequest, ApiPaymentMethod } from './types';

export interface OrderRequestPayload {
  delivery_type_id: number;
  driver_id?: number | null;
  departure_zone_id: number;
  arrival_zone_id: number;
  departure_address: string;
  arrival_address: string;
  departure_landmark?: string | null;
  arrival_landmark?: string | null;
  package_description: string;
  estimated_weight: number;
  estimated_volume?: number | null;
  declared_value: number;
  pay_now: boolean;
  payment_method_id?: number | null;
  total_delivery_fee: number;
}

// ── Delivery types ────────────────────────────────────────────
export async function listDeliveryTypes(): Promise<ApiDeliveryType[]> {
  const res = await api.get('/delivery-types');
  return unwrapList<ApiDeliveryType>(res.data);
}

export async function createDeliveryType(payload: {
  name: string;
  amount_per_kg: number;
  description?: string;
}): Promise<ApiDeliveryType> {
  const res = await api.post('/delivery-types', payload);
  return unwrapItem<ApiDeliveryType>(res.data);
}

export async function updateDeliveryType(
  id: number,
  payload: { name?: string; amount_per_kg?: number; description?: string },
): Promise<ApiDeliveryType> {
  const res = await api.put(`/delivery-types/${id}`, payload);
  return unwrapItem<ApiDeliveryType>(res.data);
}

export async function deleteDeliveryType(id: number): Promise<void> {
  await api.delete(`/delivery-types/${id}`);
}

// ── Payment methods ───────────────────────────────────────────
export async function listPaymentMethods(): Promise<ApiPaymentMethod[]> {
  const res = await api.get('/payment-methods');
  return unwrapList<ApiPaymentMethod>(res.data);
}

export async function createPaymentMethod(payload: {
  name: string;
  description?: string;
}): Promise<ApiPaymentMethod> {
  const res = await api.post('/payment-methods', payload);
  return unwrapItem<ApiPaymentMethod>(res.data);
}

export async function updatePaymentMethod(
  id: number,
  payload: { name?: string; description?: string },
): Promise<ApiPaymentMethod> {
  const res = await api.put(`/payment-methods/${id}`, payload);
  return unwrapItem<ApiPaymentMethod>(res.data);
}

export async function deletePaymentMethod(id: number): Promise<void> {
  await api.delete(`/payment-methods/${id}`);
}

// ── Order requests ────────────────────────────────────────────
export async function listOrderRequests(): Promise<ApiOrderRequest[]> {
  const res = await api.get('/order-requests');
  return unwrapList<ApiOrderRequest>(res.data);
}

export async function getOrderRequest(id: number): Promise<ApiOrderRequest> {
  const res = await api.get(`/order-requests/${id}`);
  return unwrapItem<ApiOrderRequest>(res.data);
}

export async function createOrderRequest(payload: OrderRequestPayload): Promise<ApiOrderRequest> {
  const res = await api.post('/order-requests', payload);
  return unwrapItem<ApiOrderRequest>(res.data);
}

export async function updateOrderRequestStatus(id: number, status: string): Promise<ApiOrderRequest> {
  const res = await api.patch(`/order-requests/${id}/status`, { status });
  return unwrapItem<ApiOrderRequest>(res.data);
}

export async function listDriverOrderRequests(
  driverId: number,
  status?: string,
): Promise<ApiOrderRequest[]> {
  const res = await api.get(`/drivers/${driverId}/order-requests`, {
    params: status ? { status } : undefined,
  });
  return unwrapList<ApiOrderRequest>(res.data);
}

export interface ConfirmPaymentPayload {
  payment_timing: 'before_delivery' | 'after_delivery';
  payment_method_id?: number;
  payer_phone: string;
  mobile_money_provider: 'orange' | 'mtn';
}

export async function confirmOrderPayment(
  orderId: number,
  payload: ConfirmPaymentPayload,
): Promise<ApiOrderRequest> {
  const res = await api.post(`/order-requests/${orderId}/payments/confirm`, payload);
  return unwrapItem<ApiOrderRequest>(res.data);
}
